import type { UIComponent } from "../../types/uiComponent";
import type { CitedTableArtifact } from "../../types/citationArtifact";

export type CreateChatResponse = {
	id: string;
	chat_type: string;
	created_at: string;
	updated_at: string;
};

/**
 * SSE event type constants
 */
export const SSEEvent = {
	MESSAGE: "message",
	BLOCK: "block",
	ERROR: "error",
	DONE: "done",
} as const;
export type SSEEvent = (typeof SSEEvent)[keyof typeof SSEEvent];

/**
 * Content block type constants
 */
export const BlockType = {
	TEXT: "text",
	TRACE: "trace",
	THINKING: "thinking",
	CHART: "chart",
	TABLE_OVERFLOW_PENDING: "table_overflow_pending",
	TABLE_OVERFLOW: "table_overflow",
	CHART_OVERFLOW_PENDING: "chart_overflow_pending",
	CHART_OVERFLOW: "chart_overflow",
	MODIFIED_WORKBOOK: "modified_workbook",
	CITED_TABLE_ARTIFACT: "cited_table_artifact",
	CITATION_HARNESS_REPORT: "citation_harness_report",
	SCHEMA_GRAPH: "schema_graph",
	LOAN_TAPE_PREVIEW: "loan_tape_preview",
} as const;
export type BlockType = (typeof BlockType)[keyof typeof BlockType];

/**
 * Trace event type constants
 */
export const TraceType = {
	TOOL_CALL: "tool_call",
	TOOL_RESULT: "tool_result",
	PHASE: "phase",
	ERROR: "error",
	MEMO_GENERATED: "memo_generated",
	EVIDENCE_METADATA: "evidence_metadata",
	CITATION_ARTIFACT_METADATA: "citation_artifact_metadata",
} as const;
export type TraceType = (typeof TraceType)[keyof typeof TraceType];

/**
 * Typed content blocks — the unified model for both history and stream.
 *
 * Every block carries a backend-driven ``ui_component_type`` tag the FE
 * registry keys on. Historical rows may still be backfilled on the
 * backend, but the parsed FE contract requires the field on every block.
 */
export type TextBlock = {
	type: typeof BlockType.TEXT;
	text: string;
	ui_component_type: UIComponent;
};

export type TraceBlock = {
	type: typeof BlockType.TRACE;
	trace_type: TraceType | string;
	tool?: string;
	content?: string;
	success?: boolean;
	summary?: string;
	arguments?: Record<string, unknown>;
	ui_component_type: UIComponent;
	/**
	 * Opaque id pairing a TOOL_RESULT with the TOOL_CALL it answers. The
	 * Claude arm issues parallel tool calls routinely, so matching on `tool`
	 * alone attributes result A to call B whenever two same-named calls are
	 * in flight. Absent for emitters that do not supply one.
	 */
	tool_use_id?: string | null;
	// EVIDENCE_METADATA payload — registry events + coverage report.
	// Validated at the boundary with EvidenceEventSchema before render.
	evidence?: Record<string, unknown>;
};

export type ThinkingBlock = {
	type: typeof BlockType.THINKING;
	text: string;
	turn?: number;
	block_id?: string;
	ui_component_type: UIComponent;
};

export type ChartBlock = {
	type: typeof BlockType.CHART;
	version: number;
	chart_id?: string;
	chart_type: string;
	title?: string;
	chart_data: Record<string, unknown>;
	ui_component_type: UIComponent;
};

export type TableOverflowPendingBlock = {
	type: typeof BlockType.TABLE_OVERFLOW_PENDING;
	block_id: string;
	revision: number;
	row_count: number;
	filename: string;
	ui_component_type: UIComponent;
};

export type TableOverflowBlock = {
	type: typeof BlockType.TABLE_OVERFLOW;
	block_id: string;
	revision: number;
	artifact_id: number;
	row_count: number;
	filename: string;
	ui_component_type: UIComponent;
};

export type ChartOverflowPendingBlock = {
	type: typeof BlockType.CHART_OVERFLOW_PENDING;
	block_id: string;
	revision: number;
	filename: string;
	size_bytes: number;
	chart_type: string;
	title?: string;
	ui_component_type: UIComponent;
};

export type ChartOverflowBlock = {
	type: typeof BlockType.CHART_OVERFLOW;
	block_id: string;
	revision: number;
	artifact_id: number;
	filename: string;
	size_bytes: number;
	chart_type: string;
	title?: string;
	ui_component_type: UIComponent;
};

export type ModifiedWorkbookPreview = {
	sheet: string;
	rows: unknown[][];
	row_count: number;
	col_count: number;
	truncated_cols: boolean;
};

export type ModifiedWorkbookFormulaError = {
	cell: string;
	error: string;
	[key: string]: unknown;
};

export type ModifiedWorkbookBlock = {
	type: typeof BlockType.MODIFIED_WORKBOOK;
	block_id: string;
	filename: string;
	mime_type: string;
	output_storage_uri: string;
	download_signed_url: string | null;
	output_hash: string;
	manifest_summary: Record<string, unknown>;
	recalc_status: string;
	warnings: string[];
	formula_error_summary: ModifiedWorkbookFormulaError[];
	trace_id: string | null;
	duration_ms: number;
	preview: ModifiedWorkbookPreview | null;
	hidden?: boolean;
	ui_component_type: UIComponent;
};

/**
 * Typed cited-table artifact (clean citation architecture, plan §9.3).
 * The artifact payload is delivered inline for small tables; large tables
 * would instead carry `artifact_id` + a `preview` and be fetched by id.
 */
export type CitedTableArtifactBlock = {
	type: typeof BlockType.CITED_TABLE_ARTIFACT;
	artifact_id: string;
	title?: string | null;
	artifact: CitedTableArtifact;
	/**
	 * Deterministic markdown projection of the same artifact, sent by the
	 * backend because keyed-only delivery strips the table from the message
	 * text. Render it whenever the typed block cannot be rendered (feature flag
	 * off, artifact fails schema validation) — otherwise the user sees nothing.
	 */
	fallback_markdown?: string | null;
};

export type CitationHarnessReportBlock = {
	type: typeof BlockType.CITATION_HARNESS_REPORT;
	block_id: string;
	title?: string;
	report: Record<string, unknown>;
	ui_component_type: UIComponent;
};

export type SchemaGraphNode = {
	id: string;
	name: string;
	category: "raw" | "entity" | "output";
	detail?: string;
};

export type SchemaGraphEdge = {
	source: string;
	target: string;
	label?: string;
};

/** One standardized field and the raw column that fills it in each file. */
export type SchemaMapField = {
	name: string;
	/** The new file's column. Null when the run found none. */
	column: string | null;
	/** The example file's column for the same field, when it has one. */
	example_column: string | null;
};

export type SchemaMapEntity = {
	/** The standardized table's own name — "canonical_loan". */
	name: string;
	grain: string;
	grain_key: string | null;
	/** The new file's tab that fills it. */
	source_tab: string | null;
	/** The example file's tab that fills it, when that file has such a table. */
	example_tab: string | null;
	fields: SchemaMapField[];
};

/** `source` joins onto `target`; `on` is the key pair as the SQL writes it. */
export type SchemaMapJoin = { source: string; target: string; on: string };

/**
 * The standardized schema with nothing folded away: the same picture `nodes`
 * and `edges` draw, plus the fields, and the example file's side of the
 * mapping. Absent on runs recorded before the extractor wrote it.
 */
export type SchemaMap = {
	entities: SchemaMapEntity[];
	joins: SchemaMapJoin[];
	/** Every tab of the new file, used or not. */
	source_tabs: string[];
	/** Every tab of the example file, used or not. */
	example_tabs: string[];
};

export type SchemaGraphBlock = {
	type: typeof BlockType.SCHEMA_GRAPH;
	title?: string;
	nodes: SchemaGraphNode[];
	edges: SchemaGraphEdge[];
	schema?: SchemaMap | null;
	/** Frozen spine SQL, shown behind the card's "SQL" toggle. */
	sql?: string;
	ui_component_type: UIComponent;
};

export type LineageSource = {
	tab: string;
	column: string;
	canonical_field: string;
};

export type ColumnLineage = {
	header: string;
	kind: "extracted" | "derived";
	sql: string;
	transform: string;
	sources: LineageSource[];
};

export type LoanTapePreviewColumn = {
	header: string;
	align: "left" | "right";
	lineage?: ColumnLineage | null;
};

export type LoanTapePreviewBlock = {
	type: typeof BlockType.LOAN_TAPE_PREVIEW;
	filename?: string;
	columns: LoanTapePreviewColumn[];
	rows: string[][];
	ui_component_type: UIComponent;
};

/**
 * A single "inspect column lineage" click, appended to the Audit Log as an
 * accumulating, session-only series (one entry per click, never replaced).
 * ``seq`` is the 1-based order the user clicked in.
 */
export type ColumnInspection = LoanTapePreviewColumn & {
	id: string;
	seq: number;
};

export type ContentBlock =
	| TextBlock
	| TraceBlock
	| ThinkingBlock
	| ChartBlock
	| TableOverflowPendingBlock
	| TableOverflowBlock
	| ChartOverflowPendingBlock
	| ChartOverflowBlock
	| ModifiedWorkbookBlock
	| CitedTableArtifactBlock
	| CitationHarnessReportBlock
	| SchemaGraphBlock
	| LoanTapePreviewBlock;

export type UIDocumentRef = {
	doc_id: string;
	deal_name?: string | null;
	ref_type?: "document" | "deal" | null;
	project_id?: string | null;
	filename?: string;
};

export type UIMessageStreaming = {
	generation_id: string;
	status: "pending" | "running";
	last_seq: number;
};

/**
 * UI Message type — uses ContentBlock[] for both history and streaming
 */
export type UIMessage = {
	id: string;
	role: "user" | "assistant";
	content_blocks: ContentBlock[];
	documentRefs?: UIDocumentRef[];
	streaming?: UIMessageStreaming;
	// Phase 5 evidence trail. Backend lifts the EVIDENCE_METADATA payload
	// out of content_blocks into message_metadata.evidence on persist; the
	// live stream still emits it in-band as a TraceBlock.evidence. Either
	// surface is acceptable — Chat.tsx prefers metadata when present.
	evidence?: Record<string, unknown>;
	/**
	 * The generation behind this answer ended in failure — a provider error
	 * mid-stream, a harness error, a turn that never reconciled. What streamed
	 * may read as an answer; it is not one. The UI offers a Retry that re-sends
	 * the question above it with the same document references.
	 */
	failed?: { message: string; generationId?: string };
	/**
	 * Where this message sits in the chat's live thread (DOP-434). A regenerate
	 * leaves the old answer in place as a readable version; ``live`` is false
	 * for a superseded version and for every message that followed it before
	 * the new version was produced, and ``branchOf`` names the version they
	 * belong to. Absent means live.
	 */
	thread?: UIMessageThread;
	/** This answer's place among the versions of the same question. */
	versions?: UIMessageVersions;
};

export type UIMessageThread = { live: boolean; branchOf?: number };

export type UIMessageVersions = {
	questionMessageId: number;
	/** 1-based. */
	index: number;
	total: number;
	/** Every version's persisted id, oldest first. */
	ids: number[];
};

/** Which version of each question the reader has chosen to look at. */
export type SelectedVersions = Record<number, number>;
